// Stored-images section for the verification detail page.
// - Lists images with retention reason + countdown to expiry
// - Loads bytes through the auth-protected endpoint into Object URLs (no IMG src auth issue)
// - Admin can purge all images on demand
// - Shows the per-session image access log
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Lock, Trash2, History } from 'lucide-react';

const REASON_COPY = {
  manual_review:      { label: 'Manual review',     hint: 'Stored briefly so reviewers can audit borderline cases.' },
  suspected_fraud:    { label: 'Suspected fraud',   hint: 'Retained 14 days for fraud investigations.' },
  optional_retention: { label: 'Optional retention', hint: 'Stored per organization-level policy.' },
};

function fmtRemaining(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3.6e6);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h left`;
  return `${h}h left`;
}

function formatKind(kind) {
  return String(kind || '').replaceAll('_', ' ');
}

function AccessLog({ logs }) {
  if (logs.length === 0) return null;
  return (
    <div className="mt-6 border-t border-zinc-100 pt-4" data-testid="retention-log">
      <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-2">
        <History className="w-4 h-4" strokeWidth={1.5}/> Access log
      </h3>
      <ul className="text-[11px] font-mono space-y-1 max-h-40 overflow-y-auto pr-1">
        {logs.map((e) => (
          <li key={e.id} className="flex items-baseline justify-between gap-3 border-b border-zinc-100 last:border-0 py-1">
            <span>
              <span className="text-zinc-700">{e.action}</span>
              <span className="text-zinc-400"> · {e.retention_reason || '—'}</span>
              {e.user_email && <span className="text-zinc-500"> · {e.user_email}</span>}
            </span>
            <span className="text-zinc-400">{new Date(e.ts).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ImageRetention({
  sessionId,
  variant = 'section',
  kinds,
  emptyNote,
  showEmptyNote = false,
  showAccessLog = true,
  showPurge = true,
  columns = 'grid-cols-2',
  onAvailability,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState({}); // imageId -> objectURL
  const [logs, setLogs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoaded(false);
    const a = await api.get(`/verification-sessions/${sessionId}/images`);
    setImages(a.data.images);
    const b = await api.get(`/verification-sessions/${sessionId}/image-log`);
    setLogs(b.data.events);
    setLoaded(true);
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const view = async (img) => {
    if (previews[img.id]) return;
    const r = await api.get(`/verification-sessions/${sessionId}/images/${img.id}`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    setPreviews((p) => ({ ...p, [img.id]: url }));
    // refresh log so the 'view' entry shows up
    api.get(`/verification-sessions/${sessionId}/image-log`).then(({ data }) => {
      setLogs(data.events);
      window.dispatchEvent(new CustomEvent(`image-retention-log-updated:${sessionId}`));
    }).catch(() => {});
  };

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener(`image-retention-log-updated:${sessionId}`, refresh);
    return () => window.removeEventListener(`image-retention-log-updated:${sessionId}`, refresh);
  }, [load, sessionId]);

  const purgeAll = async () => {
    if (!window.confirm('Permanently delete all stored images for this KYC verification?')) return;
    setBusy(true);
    try {
      await api.delete(`/verification-sessions/${sessionId}/images`);
      Object.values(previews).forEach((u) => URL.revokeObjectURL(u));
      setPreviews({});
      await load();
    } finally { setBusy(false); }
  };

  const filteredImages = Array.isArray(kinds) && kinds.length > 0
    ? images.filter((img) => kinds.includes(img.kind))
    : images;
  const reason = images[0]?.retention_reason;
  const reasonInfo = REASON_COPY[reason];

  useEffect(() => {
    if (loaded && typeof onAvailability === 'function') {
      onAvailability(filteredImages.length > 0);
    }
  }, [filteredImages.length, loaded, onAvailability]);

  if (variant === 'contextual') {
    if (filteredImages.length === 0) {
      if (!showEmptyNote || !emptyNote) return null;
      return (
        <p className="text-xs text-zinc-500 leading-relaxed border border-zinc-100 bg-zinc-50/60 rounded-sm px-3 py-2">
          {emptyNote}
        </p>
      );
    }

    return (
      <div className={`grid ${columns} gap-3`} data-testid="retention-images-contextual">
        {filteredImages.map((img) => (
          <div key={img.id} className="flex h-full flex-col border border-zinc-200 rounded-sm p-3" data-testid={`retention-image-${img.kind}`}>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{formatKind(img.kind)}</span>
              <span className="text-[10px] font-mono text-zinc-400">{fmtRemaining(img.expires_at)}</span>
            </div>
            {previews[img.id] ? (
              <img src={previews[img.id]} alt={formatKind(img.kind)} className="w-full flex-1 min-h-0 aspect-[4/3] object-cover border border-zinc-200 rounded-sm"/>
            ) : (
              <button onClick={() => view(img)} data-testid={`view-image-${img.kind}`}
                className="w-full flex-1 min-h-0 aspect-[4/3] border border-zinc-200 border-dashed rounded-sm grid place-items-center text-xs text-zinc-500 hover:border-zinc-400 hover:bg-zinc-50">
                Click to view
              </button>
            )}
            <p className="text-[11px] font-mono text-zinc-400 mt-2">
              {(img.size_bytes / 1024).toFixed(1)} KB · {img.mime}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'access') {
    if (images.length === 0 && logs.length === 0) return null;
    return (
      <div className="border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="retention-access-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Lock className="w-4 h-4" strokeWidth={1.5}/> Image retention access
              {reasonInfo && (
                <span data-testid="retention-reason-badge"
                  className="inline-flex h-5 items-center gap-1 border border-zinc-200 bg-white px-1.5 text-[10px] font-mono font-normal uppercase tracking-widest text-zinc-500">
                  {reasonInfo.label}
                </span>
              )}
            </h2>
            <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">
              Stored images are shown in the relevant evidence cards above. Access attempts remain logged here.
            </p>
            {reasonInfo && <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">{reasonInfo.hint}</p>}
          </div>
          {isAdmin && showPurge && images.length > 0 && (
            <button onClick={purgeAll} disabled={busy} data-testid="purge-images"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-red-300 text-red-700 rounded-sm hover:bg-red-50 disabled:opacity-50">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/> Delete now
            </button>
          )}
        </div>
        {showAccessLog && <AccessLog logs={logs}/>}
      </div>
    );
  }

  if (images.length === 0 && logs.length === 0) {
    return (
      <div className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="retention-empty">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <Lock className="w-4 h-4" strokeWidth={1.5}/> Stored images
        </h2>
        <p className="text-sm text-zinc-600 mt-2">
          No images retained for this KYC verification. idemGO discarded the buffers immediately after processing.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="retention-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4" strokeWidth={1.5}/> Stored images
            {reasonInfo && (
              <span data-testid="retention-reason-badge"
                className="inline-flex h-5 items-center gap-1 border border-zinc-200 bg-white px-1.5 text-[10px] font-mono font-normal uppercase tracking-widest text-zinc-500">
                {reasonInfo.label}
              </span>
            )}
          </h2>
          {reasonInfo && <p className="text-xs text-zinc-500 mt-1">{reasonInfo.hint}</p>}
        </div>
        {isAdmin && showPurge && images.length > 0 && (
          <button onClick={purgeAll} disabled={busy} data-testid="purge-images"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-red-300 text-red-700 rounded-sm hover:bg-red-50 disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/> Delete now
          </button>
        )}
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-3" data-testid="retention-images">
          {images.map((img) => (
            <div key={img.id} className="border border-zinc-200 rounded-sm p-3" data-testid={`retention-image-${img.kind}`}>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{formatKind(img.kind)}</span>
                <span className="text-[10px] font-mono text-zinc-400">{fmtRemaining(img.expires_at)}</span>
              </div>
              {previews[img.id] ? (
                <img src={previews[img.id]} alt={formatKind(img.kind)} className="w-full aspect-[4/3] object-cover border border-zinc-200 rounded-sm"/>
              ) : (
                <button onClick={() => view(img)} data-testid={`view-image-${img.kind}`}
                  className="w-full aspect-[4/3] border border-zinc-200 border-dashed rounded-sm grid place-items-center text-xs text-zinc-500 hover:border-zinc-400 hover:bg-zinc-50">
                  Click to view
                </button>
              )}
              <p className="text-[11px] font-mono text-zinc-400 mt-2">
                {(img.size_bytes / 1024).toFixed(1)} KB · {img.mime}
              </p>
            </div>
          ))}
        </div>
      )}

      {showAccessLog && <AccessLog logs={logs}/>}
    </div>
  );
}
